
export enum PlanType {
  FREE = 'free',
  LIGHT = 'light',
  ADVANCED = 'advanced',
  AGENCY = 'agency', // New Plan
}

export enum WidgetTab {
  HOME = 'home',
  MESSAGES = 'messages',
  HELP = 'help',
  PHONES = 'phones',
  LOCATIONS = 'locations',
  WELCOME = 'welcome',
  BOOKING = 'booking',
}

export interface FaqItem {
  id: string;
  question: string;
  answer: string;
  isVisible: boolean;
  source: 'manual' | 'ai';
}

export interface AddressItem {
  id: string;
  label: string;
  address: string;
  mapUrl: string;
}

export interface Appointment {
  id: string;
  userId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  customerMessage?: string;
  type: 'consultation' | 'session' | 'onsite';
  location?: string;
  date: string;
  time: string;
  createdAt: string;
  status: 'pending' | 'confirmed' | 'cancelled';
}

export interface WelcomeImage {
  url: string;
  link?: string;
}

export interface UserConfig {
  companyName: string;
  websiteUrl: string;
  description: string;
  contactEmail: string;
  language?: string; // Preferred language (en, el)

  // Phone Numbers
  phone1: string;
  phone1Label: string;

  phone2: string;
  phone2Label: string;

  phone3: string;
  phone3Label: string;

  mobile: string;
  mobileLabel: string;

  // Messaging Apps
  contactViber?: string;
  contactMessenger?: string;
  contactWhatsapp?: string;

  googlePlaceId: string;
  googleRating: number;
  googleReviewCount: number;
  googleReviewsUrl?: string;

  // FAQs
  enableFaq?: boolean;
  faqs: FaqItem[];

  // Locations
  addresses: AddressItem[];

  themeColor: string;
  logoUrl?: string;

  // Integrations
  googleAnalyticsId?: string;

  // Appearance
  widgetAlignment?: 'left' | 'center' | 'right';
  showBranding?: boolean;
  theme?: 'light' | 'dark';
  themeTextColor?: string; // Custom Text Color for Theme

  // Granular Color Customization
  widgetClosedBgColor?: string;
  widgetClosedIconColor?: string;
  customHomeBtnBgColor?: string;
  customHomeBtnTextColor?: string;
  mainContactBtnBgColor?: string;
  mainContactBtnTextColor?: string;

  // Tab Labels (Bottom Navigation)
  tabLabelHome?: string;
  tabLabelWelcome?: string;
  tabLabelContact?: string;
  tabLabelCallus?: string;
  tabLabelHelp?: string;

  // UI Text Labels (Global Customization)
  labelMainContactBtn?: string;
  labelQuickAnswers?: string;
  labelShowAllQuestions?: string;
  labelContactTitle?: string;
  labelLeaveMessageBtn?: string;
  labelCallBackBtn?: string; // New Label   
  labelCallUsBtn?: string;
  labelViberBtn?: string;
  labelMessengerBtn?: string;
  labelVisitUsBtn?: string;
  labelCallUsTitle?: string;
  labelFaqTitle?: string;
  labelMessagePhone?: string;
  labelMessageSendBtn?: string;
  labelMessageTitle?: string;

  // Custom Home Button
  customHomeBtnLabel?: string;
  customHomeBtnUrl?: string;

  // Booking
  enableBooking?: boolean;
  labelBookingBtn?: string;
  bookingBtnColor?: string;
  bookingBtnTextColor?: string;
  labelConsultation?: string;
  labelSession?: string;
  labelOnsite?: string;
  labelSelectLocation?: string;
  labelChooseLocation?: string;
  labelFillAllFields?: string;
  labelRequestCallback?: string;
  labelCallbackName?: string;
  labelCallbackPhone?: string;

  // Booking Logic Settings
  appointmentDuration?: number;
  availableDays?: number[];
  businessHoursStart?: string;
  businessHoursEnd?: string;
  businessHoursStartWeekend?: string;
  businessHoursEndWeekend?: string;

  // Booking Types Enabled/Disabled
  enableConsultation?: boolean;
  enableSession?: boolean;
  enableOnsite?: boolean;

  // Booking Custom Labels
  labelBookNowTitle?: string;
  labelSelectType?: string;
  descConsultation?: string;
  descSession?: string;
  descOnsite?: string;
  labelSelectDate?: string;
  labelSelectTime?: string;
  labelFormName?: string;
  labelFormEmail?: string;
  labelFormPhone?: string;
  labelFormMessage?: string;
  labelConfirmBookingBtn?: string;

  // Booking Success Screen Labels
  labelSuccessTitle?: string;
  labelSuccessMessage?: string;
  labelSeeYouOn?: string;
  labelBackToHome?: string;

  // Social Media
  socialFacebook?: string;
  socialInstagram?: string;
  socialYoutube?: string;
  socialLinkedin?: string;
  socialTiktok?: string;

  // Welcome Video & Images
  enableWelcomeMessage?: boolean;
  welcomeVideoType?: 'upload' | 'youtube' | 'tiktok';
  welcomeVideoUrl?: string;
  welcomeImages?: WelcomeImage[];

  // Analytics Reporting
  enableEmailReports?: boolean;
  trackExternalLinks?: boolean; // New Feature
  reportFrequency?: 'daily' | 'weekly' | 'monthly';
  lastReportSentAt?: string; // Track when the last automated report was sent
  enableDocumentNotes?: boolean; // New feature toggle
  enableDailyRiskReport?: boolean; // Toggle for daily client risk summary

  // AI Utilities Feature Flags
  ai_features?: {
    document_ai_enabled: boolean; // Main Toggle for Ingestion Pipeline
    auto_categorization_enabled?: boolean; // Future: Auto-tag doc types
  };

  // Legacy flat content (Migration strategy: keep for now or deprecate)
  enableAiAssistant?: boolean; // @deprecated in favor of ai_features.document_ai_enabled
}

// ------------------------------------------------------------------
// AI DOCUMENT INTELLIGENCE SCHEMA
// ------------------------------------------------------------------
export interface DocumentAIAnalysis {
  doc_id: string; // Matches parent document ID
  company_id: string; // The client ID

  // Classification
  doc_type: 'invoice' | 'receipt' | 'bank_statement' | 'contract' | 'other' | 'unknown';
  doc_date: string | null; // ISO Date
  confidence_score: number;

  // Financial Entities (if applicable)
  details: {
    issuer?: string | null; // Vendor/Sender
    recipient?: string | null; // Client/Receiver
    vat_number?: string | null; // Detected VAT/Tax ID
    invoice_number?: string | null;
  };

  // Financial Amounts
  amounts: {
    net: number;
    vat: number;
    gross: number;
    currency: string;
  };

  installment_info?: {
    is_installment: boolean;
    installment_date?: string | null;
    installment_amount?: number | null;
    installment_number?: number | null; // e.g., 1 of 12
    total_debt_amount?: number | null; // Total amount owed (Συνολικό Ποσό Οφειλής)
    payment_code?: string | null; // Payment ID (Ταυτότητα Οφειλής/Πληρωμής)
  };

  // Content
  summary: string; // 1-sentence auto-summary
  keywords: string[]; // For search indexing

  // Pivot Fields for Querying
  period?: {
    month: number;
    year: number;
  };

  // Audit
  processed_at: string;
  model_used: string;
}

export interface UserProfile {
  companyName: string;
  responsiblePerson?: string;
  vatNumber: string;
  taxOffice?: string;
  activity?: string;
  address: {
    street: string;
    city: string;
    region?: string;
    postalCode: string;
    country: string;
  };
  contact: {
    phone?: string;
    mobile: string;
    email?: string; // Business Contact Email
    website?: string; // Business Website
  };
  notes?: string;
  logoUrl?: string; // Business Logo
}

export interface User {
  id: string;
  email: string;
  role: 'user' | 'admin' | 'agency' | 'agency_member'; // Added 'agency_member' role
  agencyId?: string; // Links a user to an agency
  agencyPlan?: PlanType; // For Agency Members: The inherited plan of their parent agency
  assignedAgentIds?: string[]; // For Clients: List of Agency Members (IDs) who manage this client
  memberProfile?: { // For Agency Members: Profile details
    name: string;
    phone: string;
  };
  profile?: UserProfile; // Business Profile
  plan: PlanType;
  subscriptionStatus: 'active' | 'expired' | 'cancelled';
  subscriptionEnd: string;
  cancel_at_period_end?: boolean;
  stripe_subscription_id?: string;
  config: UserConfig;
  timezone?: string;
  currency?: string;
  dateFormat?: string;
  language?: string;
  usedCoupon?: string;
  createdAt?: string;
  emailVerified?: boolean;

  // Client Attention Indicator Fields
  attentionIndex?: number;
  attentionLevel?: 'high' | 'medium' | 'stable';
  attentionReasons?: string[];
  attentionUpdatedAt?: string;
  openObligationsCount?: number;
  unreadOpenObligationsCount?: number;
  lastLoginAt?: string;
}

export interface SystemSettings {
  aiApiKey: string;
  emailJsServiceId: string;
  emailJsTemplateId: string;
  emailJsPublicKey: string;
}

export interface EmailJsConfig {
  serviceId: string;
  templateId: string;
  publicKey: string;
}

export interface PricingTier {
  monthly: number;
  yearly: number;
}

export interface GlobalPricing {
  [PlanType.LIGHT]: PricingTier;
  [PlanType.ADVANCED]: PricingTier;
  [PlanType.AGENCY]: PricingTier;
}

export interface AnalyticsEvent {
  id: string;
  userId: string;
  eventType: 'email_form' | 'phone_click' | 'widget_open' | 'booking_request' | 'call_back' | 'external_link_click';
  timestamp: string;
  details: string;
  source: string;
  medium: string;
  campaign: string;
  referrer: string;
  path: string;
}

export interface BillingProfile {
  id: string; // Firestore ID
  type: 'individual' | 'company';
  name: string;
  vatNumber?: string;
  vatValid?: boolean;
  taxOffice?: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  email: string;
  isDefault: boolean;
  taxStatus: 'taxed_domestic' | 'eu_reverse_charge' | 'eu_taxed' | 'export_exempt';
}

export interface Payment {
  id: string; // Stripe Payment Intent ID
  amount: number;
  currency: string;
  status: string;
  date: string;
  invoicePdf?: string;
  invoiceRequestStatus: 'none' | 'pending' | 'sent';
  invoiceRequestDate?: string;
  invoiceNumber?: string;
}

// --- DOCUMENTS MODULE ---

export interface DocumentType {
  id: string;
  agencyId: string;         // Owner Agency
  name: string;             // e.g., "Balance Sheet" (Default/Internal)
  nameTranslations?: {    // Localized names
    en?: string;
    el?: string;
  };
  description: string;
  category: 'financial' | 'legal' | 'tax' | 'other';
  allowedFileTypes: string[]; // e.g., ['pdf', 'xlsx']
  isRequired: boolean;
  isVisibleToClient: boolean;
  clientCanUpload?: boolean; // New: Controls if client can select this type
  aiOcrEnabled?: boolean;   // New: Controls if AI OCR should run for this type
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Document {
  id: string;
  typeId: string;           // Link to DocumentType
  typeName: string;         // Cached name for easier display
  title: string;

  agencyId: string;         // Partitioning key
  clientId: string;         // Partitioning key
  uploadedBy: string;       // User ID of uploader
  uploadedByRole: 'agency' | 'client';

  fileUrl: string;
  storagePath: string;      // Reference for deletion
  fileType: string;         // MIME type
  size: number;

  status: 'pending' | 'approved' | 'rejected' | 'archived';
  version: number;          // v1, v2...

  metadata?: {
    period?: string;        // e.g., "2024-Q1"
    periodStart?: string;
    periodEnd?: string;
    project?: string;
    notes?: boolean | string;
    hasUnreadNotesForAgency?: boolean;
    hasUnreadNotesForClient?: boolean;
  };

  // AI Analysis Data (Structured Extraction)
  ai_analysis?: DocumentAIAnalysis;

  createdAt: string;
  updatedAt: string;
  clientViewed?: boolean;
  paymentStatus?: 'paid' | 'pending'; // New field for payment tracking
}

export interface DocumentNote {
  id: string;
  documentId: string;
  userId: string;
  userName: string;
  content: string;
  createdAt: string;
  isAgency: boolean;
  memberName?: string; // For Agency Members: Display their name under the Agency Name
}
